import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const lines = ["Club Star Eventi"];

const heroPhotos = [
  { src: "/party-pista.webp", alt: "Ospiti che ballano durante una festa al Club" },
  { src: "/hero-bar.webp", alt: "Il punto bar del Club allestito per un evento" },
  { src: "/party-buffet.webp", alt: "Dettaglio dell'allestimento con luci soffuse" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setSlide((i) => (i + 1) % heroPhotos.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-screen overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0" data-testid="hero-slideshow">
        <AnimatePresence>
          <motion.img
            key={heroPhotos[slide].src}
            src={heroPhotos[slide].src}
            alt={heroPhotos[slide].alt}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
            className="absolute inset-0 h-full w-full object-cover"
            data-testid="hero-slide-image"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-[#060607]/60" />
      </motion.div>

      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 flex items-center gap-4 text-xs font-medium uppercase tracking-[0.35em] text-dusty md:text-sm"
          data-testid="hero-overline"
        >
          <span className="h-px w-10 bg-dusty/70" />
          Location esclusiva per eventi — Roma
        </motion.p>

        <h1 className="font-serif text-4xl leading-[0.95] tracking-tighter text-linen sm:text-6xl md:text-7xl" data-testid="hero-heading">
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.18, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.span>
            </span>
          ))}
          <span className="invisible block" aria-hidden="true">Eventi</span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-linen/70 md:text-lg"
          data-testid="hero-subtitle"
        >
          Diciottesimi, feste di compleanno, cerimonie ed eventi aziendali in
          una location elegante, dove ogni festa si trasforma in un sogno.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-preventivo"
            onClick={() => window.scrollToSection?.("#contatti")}
            className="group rounded-full bg-sand px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:tracking-[0.3em] hover:bg-dusty"
          >
            Richiedi un preventivo
          </button>
        </motion.div>
      </div>

      <div className="absolute bottom-8 right-6 z-10 flex gap-2 lg:right-10" data-testid="hero-slideshow-dots">
        {heroPhotos.map((p, i) => (
          <button
            key={p.src}
            data-testid={`hero-dot-${i}`}
            onClick={() => setSlide(i)}
            aria-label={`Mostra foto ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              slide === i ? "w-8 bg-linen" : "w-3 bg-linen/40 hover:bg-linen/70"
            }`}
          />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}>
          <ArrowDown className="h-5 w-5 text-linen/50" />
        </motion.div>
      </motion.div>
    </section>
  );
}
